import { FC, PointerEvent, useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@bonusly/design-system';

import { DOODLE_CANVAS_CURSOR } from '../../doodle-editor/doodle-cursor';
import { DOODLE_CANVAS, DOODLE_CANVAS_BG } from './constants';
import { renderStrokeToCtx } from './stroke-utils';
import type { DoodleStroke } from './use-doodle';

interface DoodleCanvasProps {
	strokes: DoodleStroke[];
	currentStroke: DoodleStroke | null;
	onStartStroke: (point: number[]) => void;
	onAddPoint: (point: number[]) => void;
	onEndStroke: () => void;
}

const CANVAS_WIDTH = DOODLE_CANVAS.width;
const CANVAS_HEIGHT = DOODLE_CANVAS.height;

const getDisplayPixelRatio = (): number => {
	if (typeof window === 'undefined') return 1;
	return Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
};

export const DoodleCanvas: FC<DoodleCanvasProps> = ({ strokes, currentStroke, onStartStroke, onAddPoint, onEndStroke }) => {
	const [pixelRatio] = useState(getDisplayPixelRatio);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const isDrawing = useRef(false);
	const rafRef = useRef<number | null>(null);
	const cacheCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const cachedStrokesCountRef = useRef(0);

	const getCanvasPoint = useCallback((e: PointerEvent): number[] => {
		const canvas = canvasRef.current;
		if (!canvas) return [0, 0];

		const rect = canvas.getBoundingClientRect();
		const scaleX = CANVAS_WIDTH / rect.width;
		const scaleY = CANVAS_HEIGHT / rect.height;

		const x = (e.clientX - rect.left) * scaleX;
		const y = (e.clientY - rect.top) * scaleY;
		// A mouse reports pressure 0 (only pens/touch report a real value). Left
		// at 0, `simulatePressure` turns false in the renderer and the stroke
		// collapses to a hairline / invisible line, which then trips the blank-PNG
		// guard. Normalize a falsy mouse pressure to a neutral 0.5 so mouse strokes
		// render at full width with simulated pressure.
		const pressure = e.pointerType === 'mouse' || !e.pressure ? 0.5 : e.pressure;

		return [x, y, pressure];
	}, []);

	const handlePointerDown = useCallback(
		(e: PointerEvent) => {
			e.preventDefault();
			(e.target as Element).setPointerCapture(e.pointerId);
			isDrawing.current = true;
			onStartStroke(getCanvasPoint(e));
		},
		[getCanvasPoint, onStartStroke]
	);

	const handlePointerMove = useCallback(
		(e: PointerEvent) => {
			if (!isDrawing.current) return;
			e.preventDefault();

			if (rafRef.current !== null) return;
			rafRef.current = requestAnimationFrame(() => {
				rafRef.current = null;
				onAddPoint(getCanvasPoint(e));
			});
		},
		[getCanvasPoint, onAddPoint]
	);

	const handlePointerUp = useCallback(
		(e: PointerEvent) => {
			e.preventDefault();
			if (!isDrawing.current) return;
			isDrawing.current = false;
			// Cancel any pending rAF-throttled move so it can't fire after the
			// stroke ends, then flush the release position. Without this, the
			// last (in-flight) move point is dropped and the stroke ends short
			// of where the pointer actually lifted.
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			onAddPoint(getCanvasPoint(e));
			onEndStroke();
		},
		[getCanvasPoint, onAddPoint, onEndStroke]
	);

	useEffect(() => {
		if (!cacheCanvasRef.current) {
			cacheCanvasRef.current = document.createElement('canvas');
		}

		const cacheCanvas = cacheCanvasRef.current;
		const backingWidth = CANVAS_WIDTH * pixelRatio;
		const backingHeight = CANVAS_HEIGHT * pixelRatio;
		if (cacheCanvas.width !== backingWidth || cacheCanvas.height !== backingHeight) {
			cacheCanvas.width = backingWidth;
			cacheCanvas.height = backingHeight;
			cachedStrokesCountRef.current = 0;
		}

		const cacheCtx = cacheCanvas.getContext('2d');
		if (!cacheCtx) return;
		cacheCtx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

		if (cachedStrokesCountRef.current > strokes.length || cachedStrokesCountRef.current === 0) {
			cacheCtx.fillStyle = DOODLE_CANVAS_BG;
			cacheCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
			for (const stroke of strokes) {
				renderStrokeToCtx(cacheCtx, stroke, pixelRatio);
			}
			cachedStrokesCountRef.current = strokes.length;
		} else if (strokes.length > cachedStrokesCountRef.current) {
			for (let i = cachedStrokesCountRef.current; i < strokes.length; i++) {
				renderStrokeToCtx(cacheCtx, strokes[i], pixelRatio);
			}
			cachedStrokesCountRef.current = strokes.length;
		}
	}, [pixelRatio, strokes]);

	useEffect(() => {
		const canvas = canvasRef.current;
		const cacheCanvas = cacheCanvasRef.current;
		if (!canvas || !cacheCanvas) return;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = 'high';
		ctx.drawImage(cacheCanvas, 0, 0);

		if (currentStroke) {
			ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
			renderStrokeToCtx(ctx, currentStroke, pixelRatio);
		}
	}, [strokes, currentStroke, pixelRatio]);

	return (
		<Box
			ref={containerRef}
			position="relative"
			width="100%"
			bg="white"
			overflow="hidden"
			sx={{ aspectRatio: `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}`, touchAction: 'none' }}
		>
			<canvas
				ref={canvasRef}
				width={CANVAS_WIDTH * pixelRatio}
				height={CANVAS_HEIGHT * pixelRatio}
				style={{
					width: '100%',
					height: '100%',
					cursor: DOODLE_CANVAS_CURSOR,
					display: 'block',
				}}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				onPointerLeave={handlePointerUp}
			/>
		</Box>
	);
};
